<template>
  <div class="manage-users">
    <div class="d-flex justify-content-between align-items-start">
      <h2 class="manage-users__heading">
        Our users
      </h2>
      <b-button
        v-if="!loading"
        class="btn btn-accent-1"
        @click="toggleUsersEdit($event)">
        {{ usersEditable ? "Save" : "Edit users" }}
      </b-button>
    </div>
    <div id="manageUsersItemsContainer"
         class="manage-users__items-container">
      <div v-if="loading"
           class="manage-users__loading-container">
        <div class="manage-users__loading-content">
          <h6>Loading managed users</h6>
          <font-awesome-icon icon="spinner"
                             class="icon"
                             size="lg"
                             spin></font-awesome-icon>
        </div>
      </div>

      <template v-if="users && users.length > 0">
        <table class="table b-table table-borderless manage-users__items-table">
          <thead>
            <tr>
              <th class="users-line__name">
                Name
              </th>
              <th class="users-line__email">
                Email address
              </th>
              <th class="users-line__organisationAdministrator">
                <div id="schoolAccountAdminHeader">
                  School account administrator
                </div>

                <b-tooltip target="schoolAccountAdminHeader"
                           triggers="hover"
                           variant="light">
                  <p>Can order all types of product and can manage school user accounts</p>
                </b-tooltip>
              </th>
              <th v-if="showTrainingAndConsultancyColumns"
                  class="users-line__canOrderTraining">
                Can order training / conferences
              </th>
              <th v-if="showTrainingAndConsultancyColumns"
                  class="users-line__canOrderConsultancy">
                Can order consultancy / subscriptions
              </th>
              <th class="users-line__suspendedAccount">
                Suspend account
              </th>
              <th class="users-line__actions"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in users"
                :key="user.webAccountId">
              <td class="users-line__name">
                {{ user["name"] }}
              </td>
              <td class="users-line__email">
                {{ user["email"] }}
              </td>
              <td class="users-line__organisationAdministrator">
                <b-form-group>
                  <b-form-checkbox
                    v-model="user.organisationAdministrator"
                    :aria-label="
                      user.organisationAdministrator
                        ? 'Remove user as org admin'
                        : 'Grant user as org admin'
                    "
                    :disabled="!usersEditable"
                    @change="
                      onCheckboxClick($event, user.webAccountId, 'organisationAdministrator')
                    ">
                  </b-form-checkbox>
                </b-form-group>
              </td>
              <td v-if="showTrainingAndConsultancyColumns"
                  class="users-line__canOrderTraining">
                <b-form-group>
                  <b-form-checkbox
                    v-model="user.canOrderTraining"
                    :aria-label="
                      user.canOrderTraining ? 'Disallow order tracking' : 'Allow order training'
                    "
                    :disabled="!usersEditable"
                    @change="onCheckboxClick($event, user.webAccountId, 'canOrderTraining')">
                  </b-form-checkbox>
                </b-form-group>
              </td>
              <td v-if="showTrainingAndConsultancyColumns"
                  class="users-line__canOrderConsultancy">
                <b-form-group>
                  <b-form-checkbox
                    v-model="user.canOrderConsultancy"
                    :aria-label="
                      user.canOrderConsultancy
                        ? 'Disallow to order consultancy'
                        : 'Allow to order consultancy'
                    "
                    :disabled="!usersEditable"
                    @change="onCheckboxClick($event, user.webAccountId, 'canOrderConsultancy')">
                  </b-form-checkbox>
                </b-form-group>
              </td>
              <td class="users-line__suspendedAccount">
                <b-form-group :id="'userAccountSuspendedCell_' + user.webAccountId">
                  <b-form-checkbox
                    v-model="user.suspendedAccount"
                    :aria-label="user.suspendedAccount ? 'Reinstate account' : 'Suspend account'"
                    :disabled="!usersEditable"
                    @change="onCheckboxClick($event, user.webAccountId, 'suspendedAccount')">
                  </b-form-checkbox>
                </b-form-group>

                <b-tooltip
                  v-if="user.dateAccountSuspended !== null"
                  :target="'userAccountSuspendedCell_' + user.webAccountId"
                  triggers="hover"
                  variant="light">
                  <p>Account suspended on {{ user.dateAccountSuspended }}</p>
                </b-tooltip>
              </td>

              <td class="users-line__actions">
                <b-button
                  variant="danger"
                  aria-label="Delete user"
                  class="btn-sm"
                  :disabled="!usersEditable"
                  @click="onDeleteClick(user.webAccountId)">
                  <span class="visually-hidden">
                    Delete user
                  </span>
                  <font-awesome-icon :icon="['far', 'trash-alt']">
                  </font-awesome-icon>
                </b-button>
              </td>
            </tr>
          </tbody>
        </table>

        <div class="manage-users__items-cards">
          <manage-users-card
            v-for="(item, index) in users"
            :key="index"
            :user="item"
            :users-editable="usersEditable"
            @loading="val => (loading = val[0])"
            @onOrganisationAdministratorClick="onCheckboxClick"
            @onCanOrderTrainingClick="onCheckboxClick"
            @onCanOrderConsultancyClick="onCheckboxClick"
            @onSuspendedAccountClick="onCheckboxClick"
            @onDeleteUserClick="onDeleteClick">
          </manage-users-card>
        </div>
      </template>
      <template v-else>
        <div class="manage-users__empty">
          <span>No users have been found!</span>
        </div>
      </template>
      <b-tooltip
        v-if="!usersEditable"
        target="manageUsersItemsContainer"
        triggers="click"
        variant="dark">
        <p>To edit permissions, please use Edit Users</p>
      </b-tooltip>
    </div>

    <ConfirmModal
      :show="showDeleteConfirmModal"
      heading="Delete user"
      @onConfirm="onDeleteConfirm"
      @onClose="showDeleteConfirmModal = false">
      <span>Are you sure you want to delete this user?</span>
    </ConfirmModal>
  </div>
</template>

<script>
import {
  getManagedUsersViewModel,
  managedUserUpdate,
  changeWebAccountStatus
} from "~/Scripts/resources/webAccountApiResource";
import ManageUsersCard from "~/Components/ManageUsers/ManageUserCard.vue";
import ConfirmModal from "~/Components/ConfirmModal.vue";

export default {
  name: "ManageUsers",
  components: {
    ManageUsersCard,
    ConfirmModal
  },
  data () {
    return {
      loading: false,
      users: [],
      showTrainingAndConsultancyColumns: false,
      selectedUserId: null,
      showDeleteConfirmModal: false,
      usersEditable: false
    };
  },
  computed: {},
  watch: {},
  created () {
    this.loadUsers();
  },
  methods: {
    loadUsers () {
      this.loading = true;
      getManagedUsersViewModel()
        .then( result => {
          if ( result.data ) {
            this.users = result.data.users;
            this.showTrainingAndConsultancyColumns = result.data.showTrainingAndConsultancyColumns;
          } else {
            console.log( "error loading managed users" );
          }
        } )
        .catch( error => {
          console.log( "error loading managed users" );
          throw error;
        } )
        .finally( () => {
          this.loading = false;
        } );
    },
    getUsersName ( id ) {
      let name = "";

      const user = this.users.find( us => us.id === id );
      if ( user ) {
        name = user.name;
      }
      return name;
    },
    onCheckboxClick ( event, webAccountId, updateType ) {
      managedUserUpdate( webAccountId, updateType, event )
        .then( result => {
          if ( !result ) {
            console.log( "error updating managed user" );
          }
        } )
        .catch( error => {
          console.log( "error updating managed user" );
          throw error;
        } )
        .finally( () => {
          this.loading = false;
        } );
    },
    onDeleteClick ( webAccountId ) {
      this.selectedUserId = webAccountId;
      this.showDeleteConfirmModal = true;
    },
    onDeleteConfirm () {
      this.showDeleteConfirmModal = false;
      if ( this.selectedUserId && this.users ) {
        this.loading = true;

        changeWebAccountStatus( this.selectedUserId, false )
          .then( result => {
            if ( result ) {
              this.loadUsers();
            } else {
              console.log( "error updating managed user active status" );
            }
          } )
          .catch( error => {
            console.log( "error updating managed user active status" );
            throw error;
          } )
          .finally( () => {
            this.loading = false;
            this.selectedUserId = null;
          } );
      }
    },
    toggleUsersEdit ( event ) {
      this.usersEditable = !this.usersEditable;
      event.target.blur();
    }
  }
};
</script>

<style lang="less" scoped>
@import "../../Styles/utils/colors.less";

.manage-users {
  /deep/ table {
    margin-bottom: 0;

    thead {
      background-color: #eee;
      position: -webkit-sticky;
      position: sticky;
      top: -1px;
      z-index: 2;
    }

    tbody > tr {
      td {
        vertical-align: middle;

        .form-group {
          margin-bottom: 0;
        }
      }
      border-top: 1px solid #b2d6d9;
    }
  }

  .users-line__subtitle {
    font-weight: 600;
    font-size: 0.8em;
  }

  @media (max-width: 1199px) {
    .users-line__email {
      display: none;
    }
  }

  .users-line__name {
    min-width: 160px;
  }

  .users-line__organisationAdministrator,
  .users-line__canOrderTraining,
  .users-line__canOrderConsultancy,
  .users-line__suspendedAccount {
    text-align: center;
  }

  &__loading-container {
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    display: flex;
    justify-content: center;
    position: absolute;
    z-index: 100;
    pointer-events: none;
    background-color: rgba(255, 255, 255, 0.7);
  }

  &__loading-content {
    display: flex;
    align-items: center;
    h6 {
      margin-bottom: 0;
    }

    .icon {
      margin: 10px;
    }
  }

  &__items-table {
    background-color: #fff;

    @media screen and (max-width: 991px) {
      display: none;
    }
  }

  &__items-cards {
    @media screen and (min-width: 992px) {
      display: none;
    }
  }

  &__items-container {
    max-height: 100%;
    overflow-x: auto;
    position: relative;

    @media screen and (min-width: 992px) {
      max-height: 750px;
      overflow-y: auto;
      scrollbar-width: thin;
      border: 2px solid #b2d6d9;
    }
  }

  &__empty {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
  }

  &__heading {
    letter-spacing: normal;
    font-weight: bold;
    font-size: 16pt;
    margin-top: 0;
    margin-bottom: 30px;
  }
}
</style>
